import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';

declare global {
  interface Window {
    __MSAL_INSTANCE__?: IPublicClientApplication;
    __MSAL_ACCOUNTS__?: AccountInfo[];
    __MSAL_INITIALIZED__?: boolean;
  }
}

interface MsalGlobalState {
  instance: IPublicClientApplication | null;
  accounts: AccountInfo[];
  isInitialized: boolean;
}

const msalState: MsalGlobalState = {
  instance: null,
  accounts: [],
  isInitialized: false
};

export const setMsalGlobalState = (instance: IPublicClientApplication, accounts: AccountInfo[]) => {
  console.log('[setMsalGlobalState] Setting MSAL global state:', {
    hasInstance: !!instance,
    accountsCount: accounts.length
  });
  
  msalState.instance = instance;
  msalState.accounts = accounts;
  msalState.isInitialized = true;
  
  // Also set on window for backward compatibility
  window.__MSAL_INSTANCE__ = instance;
  window.__MSAL_ACCOUNTS__ = accounts;
  window.__MSAL_INITIALIZED__ = true;
  
  console.log('[setMsalGlobalState] MSAL state updated successfully');
};

export const getMsalGlobalState = (): MsalGlobalState => {
  return { ...msalState };
};

export const clearMsalGlobalState = () => {
  console.log('[clearMsalGlobalState] Clearing MSAL global state');
  
  msalState.instance = null;
  msalState.accounts = [];
  msalState.isInitialized = false;
  
  // Clear window properties
  delete window.__MSAL_INSTANCE__;
  delete window.__MSAL_ACCOUNTS__;
  delete window.__MSAL_INITIALIZED__;
};

export const waitForMsalInitialization = (timeoutMs = 10000): Promise<MsalGlobalState> => {
  return new Promise((resolve, reject) => {
    console.log('[waitForMsalInitialization] Waiting for MSAL initialization...');
    
    if (msalState.isInitialized && msalState.instance && msalState.accounts.length > 0) {
      console.log('[waitForMsalInitialization] MSAL already initialized');
      resolve(getMsalGlobalState());
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (msalState.isInitialized && msalState.instance && msalState.accounts.length > 0) {
        console.log('[waitForMsalInitialization] MSAL initialization detected');
        clearInterval(checkInterval);
        resolve(getMsalGlobalState());
      } else if (Date.now() - startTime >= timeoutMs) {
        console.warn('[waitForMsalInitialization] Timeout waiting for MSAL initialization');
        clearInterval(checkInterval);
        reject(new Error('Timeout waiting for MSAL initialization'));
      }
    }, 100);
  });
};

export const getMsalInstanceSafely = (): IPublicClientApplication | null => {
  if (msalState.instance) {
    return msalState.instance;
  }
  
  // Fallback to window
  return window.__MSAL_INSTANCE__ || null;
};

export const getAccountsSafely = (): AccountInfo[] => {
  if (msalState.accounts.length > 0) {
    return msalState.accounts;
  }
  
  // Fallback to window
  return window.__MSAL_ACCOUNTS__ || [];
};

export const isMsalReady = (): boolean => {
  return msalState.isInitialized && 
         msalState.instance !== null && 
         msalState.accounts.length > 0;
};